import React, { createContext, useContext, useMemo } from "react";
import * as Option from "@utils/Option";
import type { OptionTs } from "@ps-bridge/interop";
import type { OrganizationSignupClient } from "@ps-bridge/clients/organizationSignup";
import { createStubOrganizationSignupClient } from "@src/purescript/interop";
import { enabledFeatures, FeaturesEnum } from "@utils/enabledFeatures";
import { requireContext } from "@contexts/requireContext";
import { DefaultOrganizationSignupClient } from "@services/clients/DefaultOrganizationSignupClient";

const OrganizationSignupClientContext = createContext<OptionTs<OrganizationSignupClient>>(Option.none());

export const OrganizationSignupClientProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const organizationSignupClient = useMemo<OrganizationSignupClient>(() => {
    if (enabledFeatures[FeaturesEnum.StubClients]) {
      return createStubOrganizationSignupClient;
    }
    return new DefaultOrganizationSignupClient();
  }, []);

  const contextValue = useMemo(() => Option.some(organizationSignupClient), [organizationSignupClient]);

  return (
    <OrganizationSignupClientContext.Provider value={contextValue}>
      {children}
    </OrganizationSignupClientContext.Provider>
  );
};

export const useOrganizationSignupClient = (): OrganizationSignupClient => {
  return requireContext(
    useContext(OrganizationSignupClientContext),
    "useOrganizationSignupClient must be used within an OrganizationSignupClientProvider",
  );
};
